/**
 * LIBELLÉ COMBINÉ des options d'une liste déroulante de filtre — « M0001 - Mission 1 ».
 *
 * Réglage `widgetProps.optionLabel = { fields: [fieldUUID, …], separator: " - " }`. Le champ qui
 * FILTRE reste `data[0]` : la valeur d'une option, donc la condition posée sur la page, ne change
 * pas. Seul ce que l'utilisateur LIT change. Filtres liés, valeurs proposées (`valueFilter`) et
 * option « Vide » s'appliquent donc sans modification.
 *
 * Trois étapes, trois fonctions :
 *  1. `ajouterChampsLibelle` — la requête du filtre demande aussi les champs d'affichage ;
 *  2. `specLibelle`          — dit à la mise en forme quelles colonnes de la réponse les portent ;
 *  3. `construireLibelles`   — rend `{ valeur: libellé }`, ces colonnes n'entrant PAS dans les
 *     options (sinon les codes deviendraient eux-mêmes sélectionnables).
 *
 * ⚠️ Module volontairement LÉGER, sans import : l'écran de configuration et les tests n'ont pas à
 * charger tout le graphe des widgets.
 */

/** Clé de sortie d'un champ d'affichage : déterministe, distincte de tout `valueId`. */
const sortieLibelle = index => `optionLabel_${index}`

/** Marqueur d'une partie du libellé qui EST le champ filtré : lue dans la colonne de la valeur. */
export const PARTIE_VALEUR = "__valeurDuFiltre__"

const SEPARATEUR_DEFAUT = " - "
/** Jointure de valeurs DISTINCTES d'une même partie — deux missions homonymes, deux codes. */
const JOINTURE_HOMONYMES = " / "

const champFiltre = widgetInfo => {
  const d0 = widgetInfo?.data?.[0]
  return d0?.uuid || d0?.id || null
}

/**
 * Le réglage est-il APPLICABLE à ce widget ?
 *
 * ⚠️ Pas sur un filtre multi-valeurs (`multiValueSeparator`) : une cellule y porte plusieurs
 * personnes, éclatées en autant d'options. Un libellé combiné n'aurait plus de sens par personne.
 */
export const libelleCombineActif = widgetInfo =>
  widgetInfo?.widgetType === "dropdownList" &&
  !widgetInfo?.widgetProps?.multiValueSeparator &&
  Array.isArray(widgetInfo?.widgetProps?.optionLabel?.fields) &&
  widgetInfo.widgetProps.optionLabel.fields.length > 0 &&
  Boolean(champFiltre(widgetInfo))

/**
 * Ajoute à la requête ETL d'un filtre les champs d'affichage, dans la sélection ET le groupement.
 *
 * La requête d'une liste déroulante groupe sur son champ unique : une colonne sélectionnée hors du
 * groupement serait refusée par le moteur. Le moteur rend alors des COUPLES distincts (vérifié le
 * 01/10/2026 : 10 couples Code/Libellé pour 10 missions, sur un cube de 38 lignes).
 *
 * Le champ filtré lui-même n'est pas redemandé : sa colonne est déjà dans la réponse.
 *
 * @returns une nouvelle requête ; la requête reçue est rendue telle quelle si le réglage ne s'applique pas
 */
export const ajouterChampsLibelle = (requete, widgetInfo) => {
  if (!libelleCombineActif(widgetInfo) || !requete) return requete
  const filtre = champFiltre(widgetInfo)
  const champs = widgetInfo.widgetProps.optionLabel.fields
  const selection = [...(requete.selectAdvanced ?? [])]
  const groupement = [...(requete.groupByFieldUUIDs ?? [filtre])]
  champs.forEach((champ, i) => {
    if (!champ || champ === filtre) return
    selection.push({ fieldUUID: champ, outputUUID: sortieLibelle(i), aggFunction: "" })
    if (!groupement.includes(champ)) groupement.push(champ)
  })
  return { ...requete, selectAdvanced: selection, groupByFieldUUIDs: groupement }
}

/**
 * Description, pour la mise en forme, des colonnes qui portent le libellé.
 *
 * @returns `{ sortieValeur, parties, separateur }`, ou `null` si le réglage ne s'applique pas
 */
export const specLibelle = widgetInfo => {
  if (!libelleCombineActif(widgetInfo)) return null
  const filtre = champFiltre(widgetInfo)
  const d0 = widgetInfo.data[0]
  const { fields, separator } = widgetInfo.widgetProps.optionLabel
  return {
    sortieValeur: d0?.valueId || d0?.id,
    parties: fields.map((champ, i) => (champ === filtre ? PARTIE_VALEUR : sortieLibelle(i))),
    separateur: typeof separator === "string" ? separator : SEPARATEUR_DEFAUT,
  }
}

/** Les colonnes de la réponse qui ne servent QU'AU libellé, et ne doivent pas devenir des options. */
export const sortiesLibelle = spec =>
  new Set((spec?.parties ?? []).filter(p => p !== PARTIE_VALEUR))

const vide = v => v === null || v === undefined || String(v).trim() === ""

/**
 * Libellé de chaque valeur du filtre, d'après les lignes de la réponse.
 *
 * Règles (décisions du 01/10/2026) :
 *  - une partie vide est OMISE, sans séparateur orphelin ;
 *  - deux lignes de même valeur (homonymes) JOIGNENT leurs parties distinctes par « / » :
 *    « M0001 / M0012 - Mission 1 ». Choisir l'option filtre les deux, comme sans libellé combiné ;
 *  - une valeur dont toutes les parties sont vides n'a pas de libellé : l'option garde sa valeur.
 *
 * @returns `{ [valeur en chaîne]: libellé }`
 */
export const construireLibelles = (lignes, spec) => {
  if (!spec || !Array.isArray(lignes)) return {}
  const parValeur = new Map()
  for (const ligne of lignes) {
    const valeur = ligne?.[spec.sortieValeur]
    if (vide(valeur)) continue
    const cle = String(valeur)
    if (!parValeur.has(cle)) parValeur.set(cle, spec.parties.map(() => new Set()))
    const ensembles = parValeur.get(cle)
    spec.parties.forEach((partie, i) => {
      const brute = partie === PARTIE_VALEUR ? valeur : ligne?.[partie]
      if (!vide(brute)) ensembles[i].add(String(brute).trim())
    })
  }
  const libelles = {}
  for (const [cle, ensembles] of parValeur) {
    const morceaux = ensembles
      .map(e => [...e].sort((a, b) => a.localeCompare(b)).join(JOINTURE_HOMONYMES))
      .filter(m => m !== "")
    if (morceaux.length) libelles[cle] = morceaux.join(spec.separateur)
  }
  return libelles
}
